<template>
    <div :class="['flex flex-col flex-1', editMode ? 'pointer-events-none opacity-80' : '']">
        <!--Header-->
        <div v-if="header" class="flex rounded-tl-xl rounded-tr-xl rounded-br-xl bg-gradient-to-t from-white to-blue-500 flex-row justify-between items-center">
            <p class="w-1/2 bg-white p-5 pb-1 pr-4 rounded-tr-xl rounded-tl-xl text-base font-bold text-black" :class="size === 'large' ? 'text-lg pl-10' : ''">
                {{ heading }}
            </p>
            <div class="w-1/2 h-0 flex justify-center items-center gap-3 rounded-tl-none rounded-xl group py-7 pl-5 bg-dashboard-primary">
                <router-link v-if="!editMode" to="dashboard/markets">
                    <p class="text-xxs text-dashboard-primary-grey group-hover:scale-105 duration-300">
                        {{ $t('plain_all') + ' ' + $t('nav_analysis') }}
                    </p>
                </router-link>
                <router-link v-if="!editMode" to="dashboard/markets">
                    <i class="fi fi-rr-arrow-right flex align-middle text-dashboard-primary-grey group-hover:scale-105 duration-300"></i>
                </router-link>
            </div>
        </div>
        <div
            :class="[
                'flex z-[15] gap-3 px-6 w-full bg-white',
                header ? 'rounded-tr-xl' : 'rounded-t-xl',
                !searchHeader && !filterHeader && !header && dateHeader ? 'justify-between pt-5 flex-row' : 'flex-col'
            ]"
        >
            <p v-if="title" :class="['bg-white text-md text-md font-bold text-black', header ? 'py-6' : 'p-2']">
                {{ title }}
            </p>
            <div class="flex items-center gap-1">
                <div v-if="searchHeader || filterHeader" class="flex flex-col items-center z-[15] w-full gap-2">
                    <!-- Search field -->
                    <div
                        v-if="searchHeader && analysesFilterQuery !== FilterQuery.SONDERNACHRICHTEN && analysesFilterQuery !== FilterQuery.FAVORITEN"
                        :class="[{ 'pointer-events-none': editMode }, 'w-full group flex items-center', header ? 'pt-6' : '']"
                    >
                        <input
                            v-model="searchQueryAnalysis"
                            type="search"
                            name="zielzonen"
                            :placeholder="$t('plain_search')"
                            class="input block p-2 pl-4 appearance-none focus:outline-none bg-dashboard-primary rounded-l-xl text-xxs flex-1 group-hover:shadow-md duration-300 transition"
                            :class="{ 'text-transparent': editMode, 'text-dashboard-lightGray placeholder-transparent': editMode }"
                            @input="search"
                        />
                        <i
                            class="fi fi-rr-search bg-dashboard-primary px-3 rounded-r-xl py-2 text-xxs border-l group-hover:shadow-md duration-300 transition"
                            :class="editMode ? 'invisible' : 'text-dashboard-lightGray'"
                        />
                    </div>

                    <!-- Dropdown -->
                    <div
                        v-if="filterHeader"
                        :class="[
                            { 'pointer-events-none': editMode },
                            'text-xxs text-dashboard-lightGray bg-dashboard-primary rounded-xl z-[3] w-full relative hover:shadow-md duration-300 transition',
                            analysesFilterQuery === FilterQuery.FAVORITEN || analysesFilterQuery === FilterQuery.SONDERNACHRICHTEN ? 'mt-6' : ''
                        ]"
                    >
                        <button
                            class="flex items-center justify-between rounded-xl px-5 py-2 w-full"
                            :class="{ 'pointer-events-none': editMode }"
                            @click="filterDropdownOpen = !filterDropdownOpen"
                        >
                            <span class="px-2" :class="{ 'text-transparent': editMode }">
                                {{ $t(analysesFilterQuery) }}
                            </span>
                            <i :class="editMode ? 'invisible' : filterDropdownOpen ? 'fi fi-rr-angle-small-up text-right' : 'fi fi-rr-angle-small-down text-right'"></i>
                        </button>

                        <!-- Dropdown Menu -->
                        <div v-if="filterDropdownOpen && !editMode" class="bg-white text-xs text-dashboard-primary-grey shadow-md absolute left-0 rounded-xl mt-1 p-2 w-full">
                            <p
                                v-if="['dsu', 'dmu', 'wmu'].some((type) => analyses.map((item) => item.contentstreamType.name).includes(type))"
                                class="cursor-pointer px-3 py-2 hover:bg-blue-light rounded-lg"
                                @click="
                                    analysesFilterQuery = FilterQuery.SCHRIFTLICH;
                                    filterDropdownOpen = !filterDropdownOpen;
                                "
                            >
                                {{ $t('contenstream_category_written') }}
                            </p>
                            <p
                                class="cursor-pointer px-3 py-2 hover:bg-blue-light rounded-lg"
                                @click="
                                    analysesFilterQuery = FilterQuery.KURZNACHRICHTEN;
                                    filterDropdownOpen = !filterDropdownOpen;
                                    getShortAnalyses();
                                "
                            >
                                {{ $t('contenstream_category_short') }}
                            </p>
                            <p
                                v-if="analyses.map((item) => item.contentstreamType.name).includes('webinar')"
                                class="cursor-pointer px-3 py-2 hover:bg-blue-light rounded-lg"
                                @click="
                                    analysesFilterQuery = FilterQuery.VIDEO;
                                    filterDropdownOpen = !filterDropdownOpen;
                                "
                            >
                                {{ $t('contenstream_category_webinar') }}
                            </p>
                            <p
                                v-if="!props.productGroup"
                                class="cursor-pointer px-3 py-2 hover:bg-blue-light rounded-lg"
                                @click="
                                    analysesFilterQuery = FilterQuery.SONDERNACHRICHTEN;
                                    filterDropdownOpen = !filterDropdownOpen;
                                    getSpecialAnalyses();
                                "
                            >
                                {{ $t('contenstream_category_special') }}
                            </p>
                            <p
                                class="cursor-pointer px-3 py-2 rounded-b-xl hover:bg-blue-light rounded-lg"
                                @click="
                                    analysesFilterQuery = FilterQuery.FAVORITEN;
                                    filterDropdownOpen = !filterDropdownOpen;
                                    getFavoriteContentstreams();
                                "
                            >
                                {{ $t('contenstream_category_favorites') }}
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="dateHeader && analysesFilterQuery !== FilterQuery.SONDERNACHRICHTEN && analysesFilterQuery !== FilterQuery.FAVORITEN" class="relative">
                    <i
                        :class="[
                            publishedAtQuery.length > 0 ? 'rounded-t-xl' : 'rounded-xl',
                            header ? 'mt-6' : '',
                            'fi fi-rr-calendar-clock text-lg font-bold text-dashboard-primary-grey w-12 h-10  bg-dashboard-primary cursor-pointer flex justify-center items-center hover:scale-105 hover:shadow-md duration-300'
                        ]"
                        @click="filterOpen = !filterOpen"
                    />

                    <i
                        v-if="publishedAtQuery.length > 0"
                        class="z-40 fi fi-rr-cross top-0 right-[-8px] text-xxs font-bold text-dashboard-primary-grey bg-dashboard-primary w-12 h-10 rounded-b-xl cursor-pointer flex justify-center items-center"
                        @click="
                            search();
                            publishedAtQuery = [];
                        "
                    />

                    <div :class="['absolute top-0 shadow-xl duration-300 z-20 right-0', filterOpen ? 'block' : 'hidden']">
                        <Datepicker
                            v-model="publishedAtQuery"
                            :model-value="publishedAtQuery"
                            :model-type="calendarModelType"
                            class="!m-0"
                            :locale="calendarLocale"
                            :select-text="$t('plain_select')"
                            :enable-time-picker="false"
                            :range="true"
                            inline
                            auto-apply
                            :monday-first="true"
                            :on-click-outside="() => (filterOpen = false)"
                            @update:model-value="search"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!--Contentstreams-->
        <div
            ref="contentstreamsContainer"
            class="rounded-b-xl bg-white flex flex-col flex-1 h-auto border-b-8 border-white"
            :class="(size === 'large' ? 'px-10' : ' px-6', editMode ? ' overflow-hidden' : 'overflow-y-scroll')"
        >
            <!-- The sticky element with a higher z-index -->
            <Loading :loading="loading">
                <div class="bg-gradient-to-b from-white to-transparent p-6 sticky top-0 z-10"></div>

                <div
                    v-if="analysesFilterQuery !== FilterQuery.SONDERNACHRICHTEN && analysesFilterQuery !== FilterQuery.FAVORITEN"
                    class="flex flex-col gap-3 pb-4 h-full text-xxs mt-[-24px] mx-6"
                >
                    <template v-for="productData in getTabData()" :key="reloadKey">
                        <ContentstreamsItem :contentstream="productData" :edit-mode="editMode" />
                    </template>
                    <div v-if="loadingContentstreams" class="flex justify-center items-center mt-6 pb-2.5">
                        <FontAwesomeIcon :icon="['fas', 'circle-notch']" class="animate-spin transition-all ease-in-out duration-300 text-lg"> </FontAwesomeIcon>
                        <p class="text-dashboard-primary-grey text-xxs ml-2">{{ $t('more_xxx_loading', { itemName: $t('plain_analysis_plural') }) }}</p>
                    </div>

                    <no-item-found v-if="analyses.length === 0 && (analysesFilterQuery !== '' || publishedAtQuery.length !== 0)" class="h-auto" :item-name="$t('plain_analysis')" />

                    <router-link
                        v-for="productData in specialAnalyses"
                        v-if="analysesFilterQuery === FilterQuery.SONDERNACHRICHTEN"
                        class="justify-between gap-2 px-4 rounded-xl hover:scale-y-105 duration-300 items-center bg-dashboard-primary"
                        :class="editMode ? 'odd:opacity-40 bg-dashboard-primary' : 'shadow-md'"
                        :to="getPath(productData)"
                    >
                        <div class="flex flex-row text-xs items-center">
                            <span class="mt-3 mr-2">
                                <i class="fi fi-tr-square-star text-6xl rounded-xl"></i>
                            </span>

                            <span>
                                <strong v-if="productData?.title">
                                    {{ $translate(productData.title) }}
                                </strong>
                                <small class="block text-xxs text-dashboard-primary-grey">
                                    {{ formatDateByRegion($moment(productData.published_at)) }}
                                </small>
                            </span>
                        </div>
                    </router-link>

                    <template v-for="productData in favouriteAnalyses" v-if="analysesFilterQuery === FilterQuery.FAVORITEN">
                        <ContentstreamsItem :contentstream="productData" :edit-mode="editMode" />
                    </template>
                    <div v-if="noMoreContentstreams">
                        <p class="text-dashboard-primary-grey text-xxs ml-2 text-center">{{ $t('plain_analyzes_ available') }}</p>
                    </div>
                    <div>
                        <p class="text-dashboard-primary-grey text-xxs ml-2 text-center">
                            {{ $t('as_of_date') + ': ' + formatDateByRegion($moment(lastContentstream?.published_at)) }}
                        </p>
                        <p
                            v-if="stopContentstreamLoading && !loadingContentstreams && !noMoreContentstreams"
                            class="text-dashboard-primary-grey text-xxs ml-2 text-center cursor-pointer"
                            @click="continueSearch()"
                        >
                            {{ $t('continue_search') }}
                        </p>
                    </div>
                </div>
            </Loading>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import api from '../../api.js';
import { formatDateByRegion } from '../../helpers';
import Loading from '../miscellaneous/Loading.vue';
import ContentstreamsItem from './ContentstreamsItem.vue';
import NoItemFound from './noItemFound.vue';

const props = defineProps({
    data: Object,
    specialAnalyses: Object,
    editMode: Boolean,
    heading: String,
    header: Boolean,
    filterHeader: Boolean,
    searchHeader: Boolean,
    title: String,
    dateHeader: Boolean,
    productGroup: Object,
    size: String,
    filter: String
});

const store = useStore();
const contentstreams = ref([]);
const specialAnalyses = ref([]);
const favouriteAnalyses = ref([]);
const favouriteAnalysesLength = ref(0);
const analyses = ref([]);
const searchQueryAnalysis = ref('');
const filterDropdownOpen = ref(false);
const loadingContentstreams = ref(false);
const contentstreamsCurrentPage = ref();
const contentstreamsContainer = ref(null);
const filterOpen = ref(false);
const publishedAtQuery = ref([]);
const noMoreContentstreams = ref(false);
const reloadKey = ref(0);
const pageSizes = 20;
const filteredAnalyses = [];
const loading = ref(false);
const searchActive = ref(false);
const FilterQuery = {
    KURZNACHRICHTEN: 'short',
    SCHRIFTLICH: 'contenstream_category_written',
    VIDEO: 'contenstream_category_webinar',
    SONDERNACHRICHTEN: 'contenstream_category_special',
    SCHRIFTLICH_KURZNACHRICHTEN: 'short',
    FAVORITEN: 'contenstream_category_favorites'
};
const validFilters = ref(Object.values(FilterQuery));
const defaultFilter = ref(FilterQuery.SCHRIFTLICH);
const analysesFilterQuery = ref(defaultFilter);
const lastContentstream = ref(null);
const stopContentstreamLoading = ref(true);
const region = computed(() => window.meta.region_configuration.region || 'de');
const calendarLocale = computed(() => (region.value === 'de' ? 'de-DE' : 'en-GB'));
const calendarModelType = computed(() => (region.value === 'de' ? 'dd.MM.yyyy' : 'yyyy-MM-dd'));
const contentstreamsSearchCurrentPage = ref(1);
const productId = ref(null);
const scrollingFetch = ref(false);

/**
 * Retrieves the tab data based on the current filter query.
 *
 * @returns {Array} The filtered tab data with an additional `isHovered` property.
 */
const getTabData = () => {
    if (!analyses.value) return [];

    const data =
        {
            [FilterQuery.KURZNACHRICHTEN]: getShortAnalyses(),
            [FilterQuery.SCHRIFTLICH]: getTextAnalyses(),
            [FilterQuery.VIDEO]: getVideoAnalyses(),
            [FilterQuery.SONDERNACHRICHTEN]: getSpecialAnalyses(),
            [FilterQuery.SCHRIFTLICH_KURZNACHRICHTEN]: shortTextAnalyses(),
            [FilterQuery.FAVORITEN]: getFavoriteContentstreams()
        }[analysesFilterQuery.value] || analyses.value;

    return data?.map((item) => ({ ...item, isHovered: false }));
};

/**
 * Continues the search by extending the months offset and retrieving tab data.
 *
 * @return {void}
 */

const continueSearch = async () => {
    stopContentstreamLoading.value = false; // Reset to allow the loop to run

    const threeMonthsAgo = new Date(lastContentstream.value?.published_at || new Date());
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3); // Update the date threshold

    if (publishedAtQuery.value.length > 0) {
        let [day, month, year] = publishedAtQuery.value[0].split('.');
        publishedAtQuery.value[0] = new Date(Date.UTC(year, month - 1, day));

        [day, month, year] = publishedAtQuery.value[1].split('.');
        publishedAtQuery.value[1] = new Date(Date.UTC(year, month - 1, day));
    }

    while (!stopContentstreamLoading.value) {
        await dispatchPastContentstreams();

        if (lastContentstream.value) {
            const lastContentstreamDate = new Date(lastContentstream.value.published_at);

            if (lastContentstreamDate <= threeMonthsAgo) {
                stopContentstreamLoading.value = true; // Exit the loop if the condition is met
                break;
            }
        }

        // Small delay to prevent rapid firing of requests
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
};

/**
 * Retrieves the favorite contentstreams.
 *
 * @returns {Array} The favorite contentstreams.
 */
const getFavoriteContentstreams = () => {
    if (analysesFilterQuery.value === FilterQuery.FAVORITEN) {
        loadingContentstreams.value = true;

        if (store.getters['settings/isContentLoaded']('favourites').data?.length === favouriteAnalysesLength.value) {
            favouriteAnalyses.value = store.getters['contentstreams/getByKey']('favourites').data;
        } else {
            // Dispatch action to load data if not loaded yet
            store
                .dispatch('contentstreams/favourites', {
                    params: {
                        'sort[by]': 'published_at',
                        'sort[order]': 'desc',
                        page_size: 50,
                        page: 1,
                        key: 'favourites'
                    }
                })
                .then(async () => {
                    loadingContentstreams.value = false;
                    favouriteAnalyses.value = store.getters['contentstreams/getByKey']('favourites').data;
                    favouriteAnalysesLength.value = favouriteAnalyses.value.length;
                    reloadKey.value++;
                });
        }

        return favouriteAnalyses.value;
    }
};

/**
 * Retrieves the special analyses.
 *
 * @returns {Array} The special analyses.
 */
const getSpecialAnalyses = () => {
    if (analysesFilterQuery.value === FilterQuery.SONDERNACHRICHTEN && loadingContentstreams.value === false && specialAnalyses.value.length === 0) {
        loadingContentstreams.value = true;

        if (store.getters['settings/isContentLoaded']('specials')) {
            specialAnalyses.value = store.getters['contentstreams/getByKey']('specials').data;
        } else {
            // Dispatch action to load data if not loaded yet
            store
                .dispatch('contentstreams/specials', {
                    params: {
                        'sort[by]': 'published_at',
                        'sort[order]': 'desc',
                        page_size: 15,
                        page: 1,
                        key: 'specials'
                    }
                })
                .then(() => {
                    loadingContentstreams.value = false;
                    specialAnalyses.value = store.getters['contentstreams/getByKey']('specials').data;
                });
        }

        return specialAnalyses.value;
    }
};

/**
 * Filters the analyses based on the type and returns the filtered analyses or a message if none are found.
 *
 * @param {String} type - The type of analyses to filter.
 * @param {Number} limit - The limit for the number of analyses to return.
 * @return {Array} The filtered analyses or a message if none are found.
 */
const getAnalyses = (type, limit) => {
    const filtered = analyses.value.filter((contentstream) => {
        if (type === FilterQuery.KURZNACHRICHTEN) return contentstream.contentstreamType.name === 'short';

        if (type === FilterQuery.SCHRIFTLICH) return contentstream.contentstreamType.name !== 'webinar' && contentstream.contentstreamType.name !== 'short';

        if (type === FilterQuery.VIDEO) return contentstream.contentstreamType.name === 'webinar';

        if (type === FilterQuery.SCHRIFTLICH_KURZNACHRICHTEN) return contentstream.contentstreamType.name !== 'webinar';

        return false;
    });

    if (filtered.length <= limit && contentstreamsCurrentPage.value < store.getters['contentstreams/all'].dashboard?.meta?.last_page && analysesFilterQuery.value === type) {
        waitForContentLoading('contentstreams', () => {
            if (type === FilterQuery.KURZNACHRICHTEN || type === FilterQuery.VIDEO) {
                dispatchPastContentstreams();
            } else if (type === FilterQuery.SCHRIFTLICH) {
                getMoreContentstreams();
            }
        });
    }

    return filtered;
};

/**
 * Retrieves the short analyses.
 *
 * @return {Array} The short analyses.
 */
const getShortAnalyses = () => getAnalyses(FilterQuery.KURZNACHRICHTEN, 1);

/**
 * Retrieves the text analyses.
 *
 * @return {Array} The text analyses.
 */
const getTextAnalyses = () => getAnalyses(FilterQuery.SCHRIFTLICH, 1);

/**
 * Retrieves the video analyses.
 *
 * @return {Array} The video analyses.
 */
const getVideoAnalyses = () => getAnalyses(FilterQuery.VIDEO, 1);

/**
 * Retrieves the short text analyses.
 *
 * @return {Array} The short text analyses.
 */
const shortTextAnalyses = () => getAnalyses(FilterQuery.SCHRIFTLICH_KURZNACHRICHTEN, 1);
/**
 * Generates the path for a given contentstream based on its type.
 *
 * @param {Object} contentstream - The contentstream object.
 * @return {string} The generated path for the contentstream.
 */
const getPath = (contentstream) => {
    if (!contentstream) return '';

    if (analysesFilterQuery.value === FilterQuery.SONDERNACHRICHTEN) {
        return '/dashboard/markets-analysis/' + contentstream.id;
    } else if (contentstream.contentstreamType.name && contentstream.contentstreamType.name === FilterQuery.VIDEO) {
        return '/dashboard/markets-videoanalysis/' + contentstream.id;
    } else {
        return '/dashboard/markets-analysis/' + contentstream.id;
    }
};

/**
 * Retrieves and filters the contentstreams.
 *
 * @returns {Promise<void>} A promise that resolves when the contentstreams are retrieved and filtered.
 */
const getContentstreams = async () => {
    contentstreams.value = store.getters['contentstreams/all'].dashboard.data
        .sort((a, b) => new Date(b.published_at) - new Date(a.published_at).filter)
        .filter((item) => !props.productGroup || item?.product?.productGroups.data[0]?.id === props.productGroup?.id);
    analyses.value = contentstreams.value.filter((item) => !['newsletter', 'email-marketing'].includes(item.contentstreamType.name));

    contentstreamsCurrentPage.value = store.getters['contentstreams/all'].dashboard.meta.current_page;

    await getAnalysis();
};

/**
 * Filters and sets the analyses data.
 *
 * @return {void}
 */
const getAnalysis = () => {
    analyses.value = contentstreams.value.filter((item, index, self) => self.findIndex((t) => t.id === item.id) === index);

    productId.value = props.productGroup ? analyses.value[0].product_id : null;

    getVideoAnalyses();
    getTextAnalyses();

    if (contentstreamsContainer.value) {
        contentstreamsContainer.value.onscroll = getMoreContentstreams;
    }
};

/**
 * Waits for the content to be loaded and then executes the continue function.
 *
 * @param {string} content - The content to wait for.
 * @param {Function|null} continueFunction - The function to execute after the content is loaded.
 * @param {Array} continueFunctions - An array of functions to execute sequentially after the content is loaded.
 * @return {void}
 */
const waitForContentLoading = (content, continueFunction = null, continueFunctions = []) => {
    const interval = setInterval(() => {
        if (store.getters['settings/isContentLoaded'](content)) {
            if (continueFunctions.length > 0) {
                waitForContentLoading(continueFunctions[0], continueFunction, continueFunctions.shift);
            } else if (continueFunction) {
                continueFunction();
            }

            clearInterval(interval);
        }
    }, 10);
};

/**
 * Loads the contentstreams and sets the loading state.
 *
 * @return {void}
 */
const loadContentstreams = () => {
    waitForContentLoading('contentstreams', () => {
        getContentstreams();
        loading.value = false;
    });

    if (!store.getters['settings/isContentLoaded']('contentstreams_big')) {
        waitForContentLoading('contentstreams_big', () => {
            getContentstreams();
        });
    }

    if (!store.getters['settings/isContentLoaded']('contentstreams_huge')) {
        waitForContentLoading('contentstreams_huge', () => {
            getContentstreams();
        });
    }
};

/**
 * Dispatches past contentstreams.
 *
 * @return {void}
 */
const buildParams = () => {
    const params = {
        page_size: pageSizes,
        'sort[by]': 'published_at',
        'sort[order]': 'desc'
    };

    if (searchActive.value) {
        params.page = contentstreamsSearchCurrentPage.value;
        params.search_phrase = searchQueryAnalysis.value.toLowerCase();
        params.published_at = publishedAtQuery.value.length > 0 ? publishedAtQuery.value : [];
        params.key = 'market_search';
    } else {
        params.product_id = [productId.value];
        params.page = contentstreamsCurrentPage.value;
        params.key = props.productGroup ? 'dashboard_' + productId.value : 'dashboard';
    }

    return params;
};

const fetchContentstreams = async (params) => {
    if (store.getters['contentstreams/getByKey'](params.key)?.data?.length > 0 && !scrollingFetch.value) {
        const response = store.getters['contentstreams/getByKey'](params.key);

        return {
            data: response.data,
            lastPage: response.meta.last_page
        };
    }

    let response;
    try {
        response = await api.get(`/api/v1/contentstreams`, { params });
    } catch (error) {
        console.error('Fehler beim Abrufen der Contentstreams:', error);

        return { data: [], lastPage: 0 };
    }

    return {
        data: response.data.data,
        lastPage: response.data.meta.last_page
    };
};

const filterContentstreams = (contentstreams) => {
    if (!stopContentstreamLoading.value) return contentstreams;

    return contentstreams.filter((item) => !props.productGroup || item?.product?.productGroups.data[0]?.id === props.productGroup?.id);
};

const mergeContentstreams = (newContentstreams) => {
    analyses.value = [...analyses.value, ...newContentstreams].filter((item, index, self) => self.findIndex((t) => t.id === item.id) === index);
};

const updatePagination = (lastPage) => {
    if (searchActive.value) {
        contentstreamsSearchCurrentPage.value++;
    } else {
        contentstreamsCurrentPage.value++;
    }

    // Check if we've reached the end of available content
    if (contentstreamsCurrentPage.value > lastPage || contentstreamsSearchCurrentPage.value > lastPage) {
        noMoreContentstreams.value = true;
        stopContentstreamLoading.value = true;
    }
};

const dispatchPastContentstreams = async () => {
    if (loadingContentstreams.value) {
        return;
    }

    loadingContentstreams.value = true;

    const params = buildParams();
    const { data: newContentstreams, lastPage } = await fetchContentstreams(params);

    const filteredContentstreams = filterContentstreams(newContentstreams);
    mergeContentstreams(filteredContentstreams);

    // Update last contentstream reference
    if (filteredContentstreams.length > 0) {
        lastContentstream.value = filteredContentstreams[filteredContentstreams.length - 1];
    }

    updatePagination(lastPage);

    loadingContentstreams.value = false;
    scrollingFetch.value = false;
};
/**
 * Retrieves more contentstreams.
 *
 * @return {void}
 */
const getMoreContentstreams = () => {
    if (
        contentstreamsContainer.value &&
        contentstreamsContainer.value.scrollTop + contentstreamsContainer.value.clientHeight + 1 >= contentstreamsContainer.value.scrollHeight &&
        !loadingContentstreams.value &&
        searchActive.value === false &&
        analysesFilterQuery.value !== FilterQuery.SONDERNACHRICHTEN &&
        analysesFilterQuery.value !== FilterQuery.FAVORITEN &&
        !noMoreContentstreams.value
    ) {
        scrollingFetch.value = true;
        dispatchPastContentstreams();
    }
};

/**
 * Searches for contentstreams based on the search query and date filter.
 *
 * @return {Promise<void>} A promise that resolves when the search is complete.
 */
const search = _.debounce(async function () {
    const shouldPerformSearch = () => {
        return publishedAtQuery.value.length > 0 || searchQueryAnalysis.value !== '';
    };

    const performSearch = async () => {
        filterOpen.value = false;
        loading.value = true;
        searchActive.value = true;
        contentstreamsSearchCurrentPage.value = 1;

        const response = await api.get(`/api/v1/contentstreams`, {
            params: {
                page: contentstreamsSearchCurrentPage.value,
                page_size: 20,
                product_id: [],
                search_phrase: searchQueryAnalysis.value.toLowerCase(),
                published_at: publishedAtQuery.value.length > 0 ? publishedAtQuery.value : [],
                key: 'market_search',
                'sort[by]': 'published_at',
                'sort[order]': 'desc'
            }
        });

        loading.value = false;
        analyses.value = response.data.data;
    };

    const resetSearch = async () => {
        contentstreamsContainer.value.scrollTop = 0;
        searchActive.value = false;
        noMoreContentstreams.value = false;
        stopContentstreamLoading.value = true;

        if (props?.productGroup && filteredAnalyses.value?.length > 0) {
            analyses.value = filteredAnalyses.value;
        } else {
            await getContentstreams();
        }
    };

    if (shouldPerformSearch()) {
        await performSearch();
    } else {
        await resetSearch();
    }
}, 500);

watch(
    () => analysesFilterQuery.value,
    () => {
        contentstreamsContainer.value.scrollTop = 0;
    }
);

onMounted(() => {
    if (props.filter && validFilters.value.includes(props.filter)) {
        defaultFilter.value = props.filter;
        console.log('props.filter', props.filter);
    }

    loadContentstreams();
});
</script>
